import { List, styled } from '@mui/material';

import { Fragment, useMemo, useState } from 'react';

import { UnfoldLessIcon, UnfoldMoreIcon } from '@app-velocity/icons';

import { useI18n } from '@app-velocity/i18n';

import { Divider } from '@app-velocity/ui';

import { SingleAiResponse } from '../SingleAiResponse';

import { LoopIndicator } from './components/Indicator';
import { useLoopContext } from '../../hooks/useLoopContext';

const StyledLoopContainer = styled('li')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

const StyledLoopInsideContainer = styled('div')(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const StyledLoop = styled(List)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const ToggleButton = styled('button')(({ theme }) => ({
  backgroundColor: 'var(--background-background-color)',
  color: 'var(--text-text-secondary-color)',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  border: '1px solid var(--stroke-stroke-secondary-color)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  cursor: 'pointer',
  svg: {
    fill: 'var(--text-text-secondary-color)',
  },
}));

export const Loop = () => {
  const loop = useLoopContext();
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const { t } = useI18n();

  const toggle = () => {
    setCollapsed((prev) => !prev);
  };

  const messagesToDisplay = useMemo(() => {
    if (!loop) return [];
    const { messages, cycleLength } = loop;

    return collapsed ? messages.slice(0, cycleLength) : messages;
  }, [collapsed, loop]);

  if (!loop) return null;

  const { cycleLength } = loop;

  return (
    <StyledLoopContainer>
      {cycleLength > 1 ? (
        <LoopIndicator variant="start" />
      ) : (
        <LoopIndicator variant="step" />
      )}
      <StyledLoopInsideContainer>
        <StyledLoop disablePadding dense>
          {messagesToDisplay.map((message, index) => (
            <Fragment key={index}>
              {index === 0 || index % cycleLength === 0 ? (
                <Divider
                  noMargin
                  textAlign="left"
                  text={t('prototypes:attempt_n', {
                    index: Math.floor(index / cycleLength) + 1,
                  })}
                />
              ) : null}
              <SingleAiResponse stepNumber={index + 1} message={message} />
            </Fragment>
          ))}
        </StyledLoop>
        <ToggleButton onClick={toggle}>
          {t(collapsed ? 'prototypes:expand_loop' : 'prototypes:collapse_loop')}
          {collapsed ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
        </ToggleButton>
      </StyledLoopInsideContainer>
      {cycleLength > 1 ? <LoopIndicator variant="finish" /> : null}
    </StyledLoopContainer>
  );
};
